import { Menu, X, User, LogOut, LayoutDashboard, ShoppingBag } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
const LOGO_URL = "/logo.jpeg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/menu', label: 'Menu' },
    { path: '/puzzle', label: 'Puzzle' },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF6F0]/80 border-b border-[#2C402E]/10" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img src={LOGO_URL} alt="ANAN's Kitchen" className="w-11 h-11 rounded-full object-cover shadow-md" />
            <div className="flex flex-col">
              <span className="font-serif font-bold text-xl text-[#2C402E] leading-tight">ANAN's Kitchen</span>
              <span className="text-xs text-[#E05D3A] font-medium tracking-wide">Healthy You</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#E05D3A]'
                    : 'text-[#2C402E] hover:text-[#E05D3A]'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2 border-[#2C402E]/20 hover:border-[#E05D3A] hover:text-[#E05D3A]" data-testid="user-menu-btn">
                    <User className="w-4 h-4" />
                    <span className="max-w-[100px] truncate">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-dashboard-link">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/subscribe')} data-testid="my-subscription-link">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    My Subscription
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-[#2C402E] hover:text-[#E05D3A]" data-testid="login-btn">
                  Login
                </Button>
                <Button className="bg-[#E05D3A] hover:bg-[#C94F2E] rounded-full px-6" onClick={() => navigate('/subscribe')} data-testid="subscribe-btn">
                  Subscribe Now
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-[#2C402E]/10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-[#2C402E]" /> : <Menu className="w-6 h-6 text-[#2C402E]" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[#2C402E]/10 bg-[#FAF6F0]" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-2 px-4 rounded-lg text-sm font-medium ${
                  isActive(link.path)
                    ? 'bg-[#E05D3A]/10 text-[#E05D3A]'
                    : 'text-[#2C402E] hover:bg-[#2C402E]/5'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-[#2C402E]/10 space-y-2">
              {user ? (
                <>
                  {user.role === 'admin' && (
                    <Button
                      variant="outline"
                      className="w-full justify-start gap-2"
                      onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Admin Dashboard
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-2"
                    onClick={() => { navigate('/subscribe'); setMobileMenuOpen(false); }}
                  >
                    <ShoppingBag className="w-4 h-4" />
                    My Subscription
                  </Button>
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  >
                    Login
                  </Button>
                  <Button
                    className="w-full bg-[#E05D3A] hover:bg-[#C94F2E] rounded-full"
                    onClick={() => { navigate('/subscribe'); setMobileMenuOpen(false); }}
                  >
                    Subscribe Now
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
